
import React, { useState } from 'react';
import { authService } from '../lib/auth';
import { User } from '../types';

interface LoginModalProps {
  onLoginSuccess: (user: User | null) => void;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onLoginSuccess, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      console.log(`[Login] Pokus o přihlášení: ${email.trim()}`);
      await authService.login(email.trim(), password);
      // Success state is managed by listener in App.tsx
      onLoginSuccess(null); 
    } catch (err: any) {
      console.error("[Login] Selhání:", err.code, err.message);
      const code = err.code || '';
      
      if (code.includes('invalid-credential') || code.includes('user-not-found') || code.includes('wrong-password')) {
          setError('Neplatný e-mail nebo heslo. Zkontrolujte prosím své údaje.');
      } else if (code.includes('operation-not-allowed')) {
          setError('Metoda přihlášení není povolena v nastavení Firebase.');
      } else if (code.includes('too-many-requests')) {
          setError('Příliš mnoho neúspěšných pokusů. Zkuste to prosím za chvíli.');
      } else {
          setError(err.message || 'Přihlášení se nezdařilo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    if (!email || !password) {
        setError("Zadejte prosím e-mail a heslo.");
        return;
    }
    setLoading(true);
    try {
        await authService.register(email.trim(), password);
        onLoginSuccess(null);
    } catch (err: any) {
        console.error("[Register] Selhání:", err.code);
        if (err.code === 'auth/email-already-in-use') {
            setError('Uživatel s tímto e-mailem již existuje. Zkuste se přihlásit.');
        } else if (err.code === 'auth/weak-password') {
            setError('Heslo je příliš krátké (min. 6 znaků).');
        } else {
            setError(err.message || 'Registrace se nezdařila.');
        }
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
        <div className="px-6 py-5 bg-gradient-to-r from-red-600 to-red-700 flex justify-between items-center text-white">
          <h2 className="text-xl font-bold">Administrace</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          {error && (
            <div className="p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-200 flex items-start gap-3">
              <svg className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
              <span>{error}</span>
            </div>
          )}
          
          <div className="space-y-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">E-mail</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none transition-all bg-gray-50"
              placeholder="vash@email.cz"
            />
          </div>
          
          <div className="space-y-1">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Heslo</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none transition-all bg-gray-50"
              placeholder="••••••••"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-200 transition-all active:scale-95 disabled:opacity-50"
            >
                {loading ? 'Přihlašuji...' : 'Přihlásit se'}
            </button>
            <button
                type="button"
                onClick={handleRegister}
                disabled={loading}
                className="flex-1 py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-xl border border-gray-200 transition-all active:scale-95 disabled:opacity-50"
            >
                Registrovat
            </button>
          </div>
          <p className="text-center text-[10px] text-gray-400">Přístup povolen pouze oprávněným osobám.</p>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
